/**
 * `studio-scope.ts` — staff-person-login-studio-switcher (2026-09-27). The
 * ONE place the SPA knows which studio it is mounted in, and the one
 * definition of the header that tells the server.
 *
 * # Why this exists
 *
 * The JWT proves the PERSON, not the studio (`apps/dashboard/src/auth.ts`):
 * one address may be staff of several studios, and the studio a request acts
 * in is a per-request SELECTION the server validates against the person's
 * `staff_allowlist` memberships. The SPA carries that selection in the URL —
 * it mounts under `/s/<slug>/` (the React Router `basename`, derived from
 * `window.location.pathname` at boot in `main.tsx`) — and repeats it on every
 * `/api/*` call as `X-Layer-Studio: <slug>`. The URL is the source of truth
 * and the header is derived from it, never the other way round, so two tabs
 * on two studios cannot leak into each other: each tab's requests carry its
 * own prefix, and a switch is a FULL navigation to the other prefix (a fresh
 * boot, a fresh basename, an empty client cache).
 *
 * # Contract
 *
 *   - `STUDIO_PREFIX` matches `/s/<slug>` at the START of a pathname, and
 *     ONLY a lower-case slug: `[a-z0-9][a-z0-9-]*`. Slugs are lower-case in
 *     `studios.slug` and the server lower-cases the header before matching,
 *     so a lower-case-only pattern is the whole space of real prefixes. A
 *     mixed-case `/s/UPPER/…` is therefore NOT a studio prefix: it yields no
 *     basename, the router matches nothing under it, the `*` route bounces to
 *     `/`, and the layout canonicalizes or shows the chooser. Treating it as
 *     a prefix would have the SPA send a header the server would match
 *     case-insensitively while the URL it re-derives the basename from
 *     differs from every link it builds — one URL, two spellings.
 *   - `basenameFromPath` is what `main.tsx` hands `<BrowserRouter>`: the
 *     prefix without a trailing slash (`"/s/<slug>"`), or `""` when there is
 *     none. Every `<Link>` / `<Navigate>` / `navigate()` in the app is then
 *     basename-relative for free; only raw `<a href>` and `window.location`
 *     writes need `studioPath` / `prefixedHref` below.
 *   - `studioHeaders()` is spread into the headers of EVERY authed fetch
 *     (`api.ts`, `dispatch.ts`, `use-saga-run.ts`, `FlowEdit.tsx`). `{}` when
 *     the tab is not under a prefix — a single-studio person's un-prefixed
 *     tab still works because the server auto-resolves their only studio;
 *     a multi-studio person's gets 403 `studio_unselected` from every route
 *     but `/api/whoami`, which answers with the chooser body instead.
 *
 * Pure apart from the one seam (`currentPathname`, guarded so the module
 * loads under node), so it is unit-tested under the root `*.test.ts` runner
 * like its siblings in this directory.
 */

/** The request header carrying the studio selection (`STUDIO_HEADER` server-side, lower-cased by Node). */
export const STUDIO_HEADER_NAME = "X-Layer-Studio";

/**
 * `/s/<slug>` at the start of a pathname, followed by `/` or the end. Group 1
 * is the slug. Lower-case only — see the module comment for why a mixed-case
 * prefix must NOT match.
 */
export const STUDIO_PREFIX = /^\/s\/([a-z0-9][a-z0-9-]*)(?=\/|$)/;

/** The tab's own pathname; `""` where there is no DOM (tests). */
function currentPathname(): string {
  return typeof window === "undefined" ? "" : window.location.pathname;
}

/** `"/s/<slug>"` when the pathname carries a studio prefix, else `""`. */
export function basenameFromPath(pathname: string): string {
  const m = STUDIO_PREFIX.exec(pathname);
  return m === null ? "" : `/s/${m[1] ?? ""}`;
}

/** The slug of the studio this pathname is mounted in, or `null` outside a prefix. */
export function activeStudioSlug(pathname: string = currentPathname()): string | null {
  const m = STUDIO_PREFIX.exec(pathname);
  return m === null ? null : (m[1] ?? null);
}

/**
 * The pathname with its studio prefix removed — what the router sees as
 * `location.pathname`. Always starts with `/`: `/s/x` and `/s/x/` both strip
 * to `/`, and a pathname without a prefix is returned untouched.
 */
export function stripStudioPrefix(pathname: string): string {
  const m = STUDIO_PREFIX.exec(pathname);
  if (m === null) return pathname;
  const rest = pathname.slice(m[0].length);
  return rest.length === 0 ? "/" : rest;
}

/**
 * The absolute URL path for `innerPath` inside `slug`'s studio. `innerPath` is
 * a basename-relative path and may carry its own search + hash
 * (`"/members/1?tab=2#h"`); a value without a leading `/` gets one, so a
 * caller cannot produce `/s/xmembers`.
 */
export function studioPath(slug: string, innerPath = "/"): string {
  const inner = innerPath.startsWith("/") ? innerPath : `/${innerPath}`;
  return `/s/${slug}${inner}`;
}

/**
 * `path` under the CURRENT tab's basename — for an anchor that must stay a
 * raw `<a href>` (a `target="_blank"` link into the app, say) rather than a
 * `<Link>`, which the router prefixes itself. Outside a prefix this is `path`
 * unchanged. Never for `/api/*` or `/trainer-pay/*`: those are server routes
 * and public routes, not pages under the studio.
 */
export function prefixedHref(path: string, pathname: string = currentPathname()): string {
  const inner = path.startsWith("/") ? path : `/${path}`;
  return `${basenameFromPath(pathname)}${inner}`;
}

/**
 * The header block every authed `/api/*` call spreads into its `headers`:
 * `{ "X-Layer-Studio": slug }` inside a prefix, `{}` outside one. One
 * definition, so the header name and the "which slug" rule cannot drift
 * between the four fetch sites.
 */
export function studioHeaders(
  pathname: string = currentPathname(),
): Readonly<Record<string, string>> {
  const slug = activeStudioSlug(pathname);
  return slug === null ? {} : { [STUDIO_HEADER_NAME]: slug };
}
